import {
	applyApFirmwareFilter,
	applyApFirmwareTaskFilter,
	applySwitchFirmwareFilter,
	focusApFirmware,
	focusApFirmwareTask,
	focusSwitchFirmware,
	openApFirmwareTasksSection,
	openApFirmwaresSection,
	openSwitchFirmwaresSection,
} from './adapter';
import {
	openAddApFirmwareModal,
	openAddApFirmwareTaskModal,
	openAddSwitchFirmwareModal,
	openDeleteApFirmwareModal,
	openDeleteApFirmwareTaskModal,
	openDeleteSwitchFirmwareModal,
	openEditApFirmwareTaskModal,
	openUpgradeApFirmwareModal,
	openUpgradeSwitchFirmwareModal,
} from './modal';
import {
	resolveApFirmware,
	resolveApFirmwareTask,
	resolveSwitchFirmware,
} from './resolver';
import {openFirmwareManagementSection} from '../maintenanceTabs/adapter';

/**
 * Canvas Maintenance > Firmware Management.
 *
 * One tab holding three lists: the Access Point firmware images, the
 * scheduled tasks that push them onto Access Points, and the Switch
 * firmware images. It used to publish only the tab, so every firmware
 * request - add a switch image, see what is scheduled, which AP versions
 * are held - landed in the same place and left the user to find which of
 * the three they wanted.
 *
 * Each list is its own section, and each now also answers for its rows. The
 * three kinds are registered separately - `ap_firmware`,
 * `ap_firmware_task`, `switch_firmware` - because they are different
 * records with different tables, and the backend names which one a request is
 * about on every lookup, filter and focus. The Add forms open directly rather
 * than by pressing a button, as Reports' does.
 *
 * Access Points and Switches have the same shape; the scheduled tasks are
 * Access Point only because that is what Canvas offers - a Switch upgrade
 * starts from the image row instead, which `switch.upgrade` says and offers
 * to open.
 */
const firmwareManagementPlugin = {
	id: 'firmware_management',
	version: 3,
	commandVersion: 1,
	resource: 'firmware_management',
	operations: [
		// `options` and no `view`: both opened this same tab in the same
		// words, so retrieval held two candidates that were one answer and
		// asked which of them the user meant. The picker is the way in.
		'options',
		'view_ap_images',
		'add_ap_image',
		'search_ap_images',
		'upgrade_ap_image',
		'delete_ap_image',
		'view_ap_tasks',
		'add_ap_task',
		'search_ap_tasks',
		'edit_ap_task',
		'delete_ap_task',
		'download_ap_task',
		'view_switch_images',
		'add_switch_image',
		'search_switch_images',
		'upgrade_switch_image',
		'delete_switch_image',
	],
	dependencies: {
		plugins: ['canvas.navigation'],
		destinations: ['maintenance.dashboard'],
	},
	sections: {
		'maintenance.firmware_management': openFirmwareManagementSection,
		'maintenance.ap_firmwares': openApFirmwaresSection,
		'maintenance.ap_firmware_tasks': openApFirmwareTasksSection,
		'maintenance.switch_firmwares': openSwitchFirmwaresSection,
	},
	resolvers: {
		ap_firmware: resolveApFirmware,
		ap_firmware_task: resolveApFirmwareTask,
		switch_firmware: resolveSwitchFirmware,
	},
	filters: {
		ap_firmware: applyApFirmwareFilter,
		ap_firmware_task: applyApFirmwareTaskFilter,
		switch_firmware: applySwitchFirmwareFilter,
	},
	focusHandlers: {
		ap_firmware: focusApFirmware,
		ap_firmware_task: focusApFirmwareTask,
		switch_firmware: focusSwitchFirmware,
	},
	modals: {
		'firmware_management.add_ap_image': openAddApFirmwareModal,
		'firmware_management.add_ap_task': openAddApFirmwareTaskModal,
		'firmware_management.add_switch_image': openAddSwitchFirmwareModal,
		'firmware_management.upgrade_ap_image': openUpgradeApFirmwareModal,
		'firmware_management.upgrade_switch_image': openUpgradeSwitchFirmwareModal,
		'firmware_management.delete_ap_image': openDeleteApFirmwareModal,
		'firmware_management.delete_ap_task': openDeleteApFirmwareTaskModal,
		'firmware_management.delete_switch_image': openDeleteSwitchFirmwareModal,
		'firmware_management.edit_ap_task': openEditApFirmwareTaskModal,
	},
};

export default firmwareManagementPlugin;
